/**
 * Form wrapper components that extend shadcn/ui primitives with labels and other conveniences
 */

import * as React from 'react';
import { Input as BaseInput, type InputProps as BaseInputProps } from './input';
import { Textarea as BaseTextarea, type TextareaProps as BaseTextareaProps } from './textarea';
import { Label } from './label';
import { cn } from '@/lib/utils';
import {
  Select as RadixSelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select';

// =============================================================================
// Labeled Input
// =============================================================================

export interface LabeledInputProps extends BaseInputProps {
  label?: string;
  hint?: string;
  error?: string;
}

export const LabeledInput = React.forwardRef<HTMLInputElement, LabeledInputProps>(
  ({ label, hint, error, className, id, required, ...props }, ref) => {
    const generatedId = React.useId();
    const inputId = id || generatedId;
    
    return (
      <div className="space-y-2">
        {label && (
          <Label htmlFor={inputId} className="text-sm font-medium">
            {label}
            {required && <span className="text-destructive ml-1">*</span>}
          </Label>
        )}
        <BaseInput
          ref={ref}
          id={inputId}
          className={cn(error && 'border-destructive', className)}
          {...props}
        />
        {hint && !error && (
          <p className="text-xs text-muted-foreground">{hint}</p>
        )}
        {error && (
          <p className="text-xs text-destructive">{error}</p>
        )}
      </div>
    );
  }
);
LabeledInput.displayName = 'LabeledInput';

// =============================================================================
// Labeled Textarea
// =============================================================================

export interface LabeledTextareaProps extends BaseTextareaProps {
  label?: string;
  hint?: string;
  error?: string;
}

export const LabeledTextarea = React.forwardRef<HTMLTextAreaElement, LabeledTextareaProps>(
  ({ label, hint, error, className, id, required, ...props }, ref) => {
    const generatedId = React.useId();
    const textareaId = id || generatedId;
    
    return (
      <div className="space-y-2">
        {label && (
          <Label htmlFor={textareaId} className="text-sm font-medium">
            {label}
            {required && <span className="text-destructive ml-1">*</span>}
          </Label>
        )}
        <BaseTextarea
          ref={ref}
          id={textareaId}
          className={cn(error && 'border-destructive', className)}
          {...props}
        />
        {hint && !error && (
          <p className="text-xs text-muted-foreground">{hint}</p>
        )}
        {error && (
          <p className="text-xs text-destructive">{error}</p>
        )}
      </div>
    );
  }
);
LabeledTextarea.displayName = 'LabeledTextarea';

// =============================================================================
// Simple Select (with options prop)
// =============================================================================

export interface SelectOption {
  value: string;
  label: string;
}

export interface SimpleSelectProps {
  options: SelectOption[];
  value?: string;
  onChange?: (e: { target: { value: string } }) => void;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

export function SimpleSelect({
  options,
  value,
  onChange,
  onValueChange,
  placeholder = 'Select...',
  label,
  hint,
  error,
  required,
  disabled,
  className,
}: SimpleSelectProps) {
  const handleValueChange = (newValue: string) => {
    onValueChange?.(newValue);
    // Simulate native event for compatibility
    onChange?.({ target: { value: newValue } });
  };

  return (
    <div className="space-y-2">
      {label && (
        <Label className="text-sm font-medium">
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}
      <RadixSelect value={value} onValueChange={handleValueChange} disabled={disabled}>
        <SelectTrigger className={cn(error && 'border-destructive', className)}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </RadixSelect>
      {hint && !error && (
        <p className="text-xs text-muted-foreground">{hint}</p>
      )}
      {error && (
        <p className="text-xs text-destructive">{error}</p>
      )}
    </div>
  );
}
